/**
 * @file The one goal module: period goal, plan to date, attainment, ahead of
 * plan and the booked plus quoted projection, from decimal strings, a yearly
 * goal, a resolved period and the calendar. Consumer: every Overview goal
 * figure, through `analyticsGoalReadProgress`. The goal splits evenly by
 * calendar day in `calendar.timeZone`; today is the local date of `period.to`
 * and counts as elapsed. Arithmetic is exact bigint fractions, ratios use the
 * unrounded period goal, and only money leaving is rounded half away from zero
 * to 4 places. No goal, a target of 0 or less, no overlap or an unparsable
 * date answers `null`; a booked, quoted or target that is no decimal throws.
 */
import { DateTime, type DurationLikeObject } from 'luxon';

import { analyticsDecimalFraction, analyticsFractionDecimal } from '#types';
import type { AnalyticsFraction, AnalyticsPeriod } from '#types';

import type {
  GoalProgress,
  GoalProgressInput,
} from './analyticsOverviewTab.types';

const MILLIS_PER_DAY = 86_400_000;

const PERIOD_SPANS = {
  ytd: { years: 1 },
  qtd: { months: 3 },
  mtd: { months: 1 },
  wtd: { weeks: 1 },
  lastWeek: { weeks: 1 },
  lastMonth: { months: 1 },
} as const satisfies Record<AnalyticsPeriod, DurationLikeObject>;

const MONEY_PLACES = 4;

const fractionOf = (decimal: string): AnalyticsFraction => {
  const fraction = analyticsDecimalFraction(decimal);
  if (fraction === undefined) throw new Error(`"${decimal}" is not a decimal.`);
  return fraction;
};

const dateOf = (instant: string, timeZone: string): DateTime =>
  DateTime.fromISO(
    DateTime.fromISO(instant, { zone: timeZone }).toISODate() ?? '',
    { zone: 'utc' },
  );

const dayOf = (date: DateTime): bigint =>
  BigInt(date.toMillis() / MILLIS_PER_DAY);

const overlap = (
  start: bigint,
  end: bigint,
  goalStart: bigint,
  goalEnd: bigint,
): bigint => {
  const from = start > goalStart ? start : goalStart;
  const to = end < goalEnd ? end : goalEnd;
  return to > from ? to - from : 0n;
};

export const goalProgress = ({
  booked,
  quoted,
  goal,
  period,
  calendar,
}: GoalProgressInput): GoalProgress | null => {
  const bookedFraction = fractionOf(booked);
  const quotedFraction = quoted === null ? null : fractionOf(quoted);
  if (goal === null) return null;
  const target = fractionOf(goal.target);
  if (target.numerator <= 0n) return null;

  const start = dateOf(period.from, calendar.timeZone);
  const today = dateOf(period.to, calendar.timeZone);
  const goalStartDate = DateTime.fromISO(goal.periodStart, { zone: 'utc' });
  const goalEndDate = DateTime.fromISO(goal.periodEnd, { zone: 'utc' });
  if (
    !start.isValid ||
    !today.isValid ||
    !goalStartDate.isValid ||
    !goalEndDate.isValid
  )
    return null;
  const goalStart = dayOf(goalStartDate);
  const goalEnd = dayOf(goalEndDate);
  const yearDays = goalEnd - goalStart;
  if (yearDays <= 0n) return null;
  const periodDays = overlap(
    dayOf(start),
    dayOf(start.plus(PERIOD_SPANS[period.period])),
    goalStart,
    goalEnd,
  );
  if (periodDays === 0n) return null;
  const planDays = overlap(
    dayOf(start),
    dayOf(today.plus({ days: 1 })),
    goalStart,
    goalEnd,
  );

  const { numerator: tn, denominator: td } = target;
  const { numerator: bn, denominator: bd } = bookedFraction;
  const money = (fraction: AnalyticsFraction): string =>
    analyticsFractionDecimal(fraction, MONEY_PLACES);

  return {
    periodGoal: money({
      numerator: tn * periodDays,
      denominator: td * yearDays,
    }),
    planToDate: money({ numerator: tn * planDays, denominator: td * yearDays }),
    attainment: {
      numerator: bn * td * yearDays,
      denominator: bd * tn * periodDays,
    },
    aheadOfPlan: money({
      numerator: bn * td * yearDays - tn * planDays * bd,
      denominator: bd * td * yearDays,
    }),
    projection:
      quotedFraction === null
        ? null
        : {
            numerator:
              (bn * quotedFraction.denominator +
                quotedFraction.numerator * bd) *
              td *
              yearDays,
            denominator: bd * quotedFraction.denominator * tn * periodDays,
          },
  };
};
